import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import { ArrowLeft, ExternalLink, User, Mail, Building2, Calendar, MessageSquare, RefreshCw, Loader2 } from "lucide-react";

interface Reply {
  id: string;
  authorName: string;
  authorType: string;
  message: string;
  createdAt: string;
}

interface Ticket {
  id: string;
  whmcsTicketId: number;
  tid: string;
  subject: string;
  clientName: string | null;
  clientEmail: string | null;
  department: string | null;
  priority: string | null;
  status: string;
  admin: string | null;
  lastReplyAt: string | null;
  createdAt: string;
  updatedAt: string;
  replies: Reply[];
}

function statusColor(s: string): string {
  const k = s.toLowerCase();
  if (k.includes("open")) return "bg-blue-100 text-blue-700";
  if (k.includes("customer")) return "bg-orange-100 text-orange-700";
  if (k.includes("answered")) return "bg-green-100 text-green-700";
  if (k.includes("progress")) return "bg-yellow-100 text-yellow-700";
  if (k.includes("closed")) return "bg-gray-100 text-gray-600";
  return "bg-gray-100 text-gray-700";
}

function fmtDate(dt: string): string {
  return new Date(dt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function WhmcsTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [whmcsUrl, setWhmcsUrl] = useState<string>("");

  const load = () => {
    setLoading(true);
    api.get(`/whmcs/tickets/${id}`)
      .then(r => setTicket(r.data))
      .catch(() => setTicket(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    api.get("/whmcs/config").then(r => setWhmcsUrl(r.data?.whmcsUrl || "")).catch(() => {});
  }, []);

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;
  if (!ticket) return <div className="p-6 text-gray-400">Ticket not found</div>;

  const openInWhmcs = whmcsUrl
    ? `${whmcsUrl.replace(/\/$/, "")}/admin/supporttickets.php?action=view&id=${ticket.whmcsTicketId}`
    : null;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft size={16}/> Back to tickets
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-xs text-gray-400">#{ticket.tid || ticket.whmcsTicketId}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(ticket.status)}`}>
                {ticket.status}
              </span>
              {ticket.priority && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{ticket.priority}</span>
              )}
            </div>
            <h1 className="text-lg font-semibold text-gray-900">{ticket.subject}</h1>
          </div>
          {openInWhmcs && (
            <a href={openInWhmcs} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-xs border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50">
              <ExternalLink size={12}/> Open in WHMCS
            </a>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-600 border-t border-gray-100 pt-3">
          {ticket.clientName && (
            <div className="flex items-center gap-1.5"><User size={12} className="text-gray-400"/> {ticket.clientName}</div>
          )}
          {ticket.clientEmail && (
            <div className="flex items-center gap-1.5"><Mail size={12} className="text-gray-400"/>
              <span className="truncate">{ticket.clientEmail}</span>
            </div>
          )}
          {ticket.department && (
            <div className="flex items-center gap-1.5"><Building2 size={12} className="text-gray-400"/> {ticket.department}</div>
          )}
          <div className="flex items-center gap-1.5"><Calendar size={12} className="text-gray-400"/> {fmtDate(ticket.createdAt)}</div>
        </div>
      </div>

      {/* Replies */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <MessageSquare size={16} className="text-blue-500"/> Conversation ({ticket.replies?.length || 0})
          </h2>
          <button onClick={load}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800">
            <RefreshCw size={11}/> Refresh
          </button>
        </div>
        {!ticket.replies?.length ? (
          <div className="text-gray-400 text-sm text-center py-6">No replies yet</div>
        ) : (
          <div className="space-y-3">
            {ticket.replies.map(r => (
              <div key={r.id} className={`rounded-xl border p-4 ${
                r.authorType === "admin" ? "bg-blue-50 border-blue-100" : "bg-gray-50 border-gray-200"
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      r.authorType === "admin" ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-700"
                    }`}>{r.authorName[0]?.toUpperCase() || "?"}</div>
                    <div>
                      <div className="text-sm font-medium text-gray-800">{r.authorName}</div>
                      <div className="text-xs text-gray-400">{r.authorType}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">{fmtDate(r.createdAt)}</div>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap ml-10 leading-relaxed">
                  {r.message}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
